import { Typography } from '@mui/material';
import { AdvancedMarker, APIProvider, Map } from '@vis.gl/react-google-maps';

import {
  BRAZIL_CENTER,
  Constants,
  GOOGLE_MAP_ID,
  UF_CENTROID,
} from '../../../../../../../constants/redeProto';
import { UfCapacity } from '../types';
import { occupancyColor } from '../utils';
import { Bubble, MapFrame, Placeholder } from './styles';

const MIN_SIZE = 16;
const MAX_SIZE = 48;

/**
 * Área da bolha proporcional ao valor (raiz do valor normalizado), que é como o
 * olho compara círculos — escalar o diâmetro linearmente exagera os maiores.
 */
const bubbleSize = (value: number, max: number): number => {
  if (max <= 0 || value <= 0) return MIN_SIZE;
  return MIN_SIZE + (MAX_SIZE - MIN_SIZE) * Math.sqrt(value / max);
};

interface CapacityMapProps {
  ufs: UfCapacity[];
}

export function CapacityMap({ ufs }: CapacityMapProps): JSX.Element {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  // Sem fila em nenhuma UF a bolha passa a representar credenciados ativos,
  // senão o mapa viraria 27 pontos idênticos do tamanho mínimo.
  const maxWaitlist = Math.max(...ufs.map((item) => item.waitlist), 0);
  const valueOf = (item: UfCapacity): number =>
    maxWaitlist > 0 ? item.waitlist : item.active;
  const maxValue = Math.max(...ufs.map(valueOf), 0);

  if (!apiKey) {
    return (
      <Placeholder>
        <Typography sx={{ fontWeight: 600 }}>
          {Constants.map.unavailableTitle}
        </Typography>
        <Typography sx={{ fontSize: '13px' }}>
          {Constants.map.unavailableBody}
        </Typography>
      </Placeholder>
    );
  }

  /**
   * `uf` de `/public-notices/{id}/poles` é sigla, mas `/network/poles` usa int:
   * se o formato mudar, nenhuma bolha casaria com `UF_CENTROID` e o mapa ficaria
   * vazio sem explicação. Avisa em vez de mostrar um mapa em branco.
   */
  const positioned = ufs.filter((item) => UF_CENTROID[item.uf] != null);

  if (ufs.length > 0 && positioned.length === 0) {
    return (
      <Placeholder>
        <Typography sx={{ fontWeight: 600 }}>
          {Constants.map.unknownUfTitle}
        </Typography>
        <Typography sx={{ fontSize: '13px' }}>
          {Constants.map.unknownUfBody}
        </Typography>
      </Placeholder>
    );
  }

  return (
    <MapFrame>
      <APIProvider apiKey={apiKey} libraries={['marker']}>
        <Map
          mapId={GOOGLE_MAP_ID}
          className="rede-proto-capacity-map"
          defaultCenter={BRAZIL_CENTER}
          defaultZoom={3}
          minZoom={3}
          streetViewControl={false}
          mapTypeControl={false}
        >
          {positioned.map((item) => {
            const center = UF_CENTROID[item.uf];
            const value = valueOf(item);
            return (
              <AdvancedMarker
                key={item.uf}
                position={center}
                title={
                  `${item.uf} · ${item.poles} polos · ` +
                  `${item.waitlist} em fila · ` +
                  `${item.availableSlots} vagas`
                }
              >
                <Bubble
                  size={bubbleSize(value, maxValue)}
                  color={occupancyColor(item.occupancy)}
                >
                  {value > 0 ? value : ''}
                </Bubble>
              </AdvancedMarker>
            );
          })}
        </Map>
      </APIProvider>
    </MapFrame>
  );
}
